function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function numberValue(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function fmt(value, digits = 0, suffix = "") {
    const n = Number(value);
    if (!Number.isFinite(n)) return "-";
    return `${digits > 0 ? n.toFixed(digits) : Math.round(n)}${suffix}`;
}

function scaleNumber(n, fixed = 2) {
    if (n >= 1_000_000_000_000) return `${(n / 1_000_000_000_000).toFixed(fixed)}T`;
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(fixed)}G`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(fixed)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(fixed)}K`;
    return typeof n === "number" ? n.toFixed(fixed) : n;
}

function fmtMbps(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return "-";
    return `${scaleNumber(n * 1_000_000, 1)}bps`;
}

function plural(value, singular, pluralText = `${singular}s`) {
    return Number(value) === 1 ? singular : pluralText;
}

function normalizeRows(payload, key) {
    return Array.isArray(payload?.[key]) ? payload[key] : [];
}

function matchesSite(value, siteId) {
    if (value === null || value === undefined || siteId === null || siteId === undefined) {
        return false;
    }
    if (String(value) === String(siteId)) {
        return true;
    }
    // 64-bit hashes now arrive from CBOR as exact decimal strings, so the
    // String comparison above matches them directly. The numeric fallback
    // remains for ids read from the URL and for older/rounded payloads.
    const a = Number(value);
    const b = Number(siteId);
    return Number.isFinite(a) && Number.isFinite(b) && a === b;
}

export function priorityMeta(siteRow) {
    const urgent = numberValue(siteRow?.urgent_count);
    const watch = numberValue(siteRow?.watch_count);
    if (urgent > 0) {
        return {
            badge: "text-bg-danger",
            icon: "fa-triangle-exclamation",
            title: "Upgrade recommended",
            summary: "One or more circuits at this site regularly reached the urgent peak-pressure threshold.",
        };
    }
    if (watch > 0) {
        return {
            badge: "text-bg-warning",
            icon: "fa-arrow-up-right-dots",
            title: "Watch for upgrade",
            summary: "One or more circuits at this site regularly reached the sustained median-pressure threshold.",
        };
    }
    return {
        badge: "text-bg-success",
        icon: "fa-circle-check",
        title: "No upgrade candidates",
        summary: "No circuits at this site matched the upgrade thresholds in the selected period.",
    };
}

function metric(label, value, icon) {
    return `
        <div class="col-sm-6 col-xl-3">
            <div class="border rounded-2 p-2 h-100">
                <div class="text-muted small"><i class="fa ${icon} me-1"></i>${escapeHtml(label)}</div>
                <div class="fw-semibold text-body">${escapeHtml(value)}</div>
            </div>
        </div>
    `;
}

function levelMeta(row) {
    return row?.level === "ReallyNeedsUpgrade"
        ? { label: "Urgent", badge: "text-bg-danger", sort: 0 }
        : { label: "Watch", badge: "text-bg-warning", sort: 1 };
}

export function buildSiteUpgradeAnalysis(payload, siteId) {
    const siteRows = normalizeRows(payload, "site_rows");
    const circuitRows = normalizeRows(payload, "rows")
        .filter((row) => matchesSite(row?.parent_node, siteId))
        .sort((a, b) => {
            const levelDiff = levelMeta(a).sort - levelMeta(b).sort;
            if (levelDiff !== 0) return levelDiff;
            return numberValue(b.p90_peak_utilization_percent) - numberValue(a.p90_peak_utilization_percent);
        });
    const siteRow = siteRows.find((row) => matchesSite(row?.site_hash, siteId)) || null;

    return {
        siteId,
        capacityCircuitCount: numberValue(payload?.capacity_circuit_count),
        siteRow,
        circuitRows,
    };
}

export function renderSiteUpgradeAnalysis(target, payload, siteId, options = {}) {
    if (!target) return;
    const analysis = buildSiteUpgradeAnalysis(payload, siteId);
    const siteRow = analysis.siteRow || {
        site_hash: siteId,
        site_name: `Site ${siteId}`,
        candidate_count: 0,
        urgent_count: 0,
        watch_count: 0,
        worst_peak_utilization_percent: 0,
        worst_median_utilization_percent: 0,
    };
    const meta = priorityMeta(siteRow);
    // The compact strip/modal summary uses a neutral badge; the detailed
    // per-row Urgent/Watch chips below keep their severity colours.
    const badgeClass = options.neutralBadge ? "text-bg-secondary" : meta.badge;
    const badgeIcon = options.neutralBadge ? "fa-circle-info" : meta.icon;
    const hasCapacityData = analysis.capacityCircuitCount > 0;
    const emptyMessage = hasCapacityData
        ? "No upgrade candidates matched this site in the selected time period."
        : "No usable service-plan limits were found for this shaper, so Insight cannot rank upgrade candidates yet.";

    target.innerHTML = `
        <div class="visually-hidden" role="status" aria-live="polite">
            ${escapeHtml(summarizeSiteUpgradeAnalysis(payload, siteId))}
        </div>
        <div class="px-3 pb-3">
            <div class="d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div>
                    <span class="badge rounded-pill ${badgeClass}">
                        <i class="fa ${badgeIcon} me-1"></i>${escapeHtml(meta.title)}
                    </span>
                    <div class="mt-2 fw-semibold">${escapeHtml(siteRow.site_name || `Site ${siteId}`)}</div>
                    <div class="text-muted small">${escapeHtml(meta.summary)}</div>
                </div>
                <div class="text-muted small text-start text-lg-end">
                    <div><i class="fa fa-users me-1"></i>${fmt(siteRow.candidate_count)} candidate ${plural(siteRow.candidate_count, "circuit")}</div>
                    <div><i class="fa fa-triangle-exclamation me-1"></i>${fmt(siteRow.urgent_count)} urgent / ${fmt(siteRow.watch_count)} watch</div>
                    <div><i class="fa fa-gauge-high me-1"></i>${fmt(analysis.capacityCircuitCount)} circuits with usable plan limits</div>
                </div>
            </div>

            <div class="row g-2 mt-2">
                ${metric("Candidate circuits", fmt(siteRow.candidate_count), "fa-users")}
                ${metric("Urgent", fmt(siteRow.urgent_count), "fa-triangle-exclamation")}
                ${metric("Watch", fmt(siteRow.watch_count), "fa-arrow-up-right-dots")}
                ${metric("Worst pressure", `Peak ${fmt(siteRow.worst_peak_utilization_percent, 0, "%")} / median ${fmt(siteRow.worst_median_utilization_percent, 0, "%")}`, "fa-gauge")}
            </div>

            ${analysis.circuitRows.length === 0 ? `
                <div class="text-muted small mt-3">${escapeHtml(emptyMessage)}</div>
            ` : `
                <div class="table-responsive mt-3">
                    <table class="table table-sm table-striped align-middle mb-0">
                        <caption class="visually-hidden">Upgrade candidate circuits for ${escapeHtml(siteRow.site_name || `Site ${siteId}`)}</caption>
                        <thead>
                            <tr>
                                <th>Circuit</th>
                                <th>Plan Limit</th>
                                <th>Peak Pressure</th>
                                <th>Median Pressure</th>
                                <th>Priority</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${analysis.circuitRows.map((row) => {
                                const level = levelMeta(row);
                                const link = `shaper_circuit.html?circuit=${encodeURIComponent(row.circuit_hash)}&nodeId=${encodeURIComponent(window.nodeId || "")}`;
                                return `
                                    <tr>
                                        <th scope="row"><a href="${link}" class="text-decoration-underline"><i class="fa fa-arrow-up-right-from-square me-1" aria-hidden="true"></i>${escapeHtml(row.circuit_name || row.circuit_hash)}</a></th>
                                        <td>Download ${fmtMbps(row.download_limit_mbps)} / Upload ${fmtMbps(row.upload_limit_mbps)}</td>
                                        <td>90th percentile peak ${fmt(row.p90_peak_utilization_percent, 0, "%")}<br><span class="text-muted small">Maximum peak ${fmt(row.max_peak_utilization_percent, 0, "%")}</span></td>
                                        <td>50th percentile median ${fmt(row.p50_median_utilization_percent, 0, "%")}<br><span class="text-muted small">Maximum median ${fmt(row.max_median_utilization_percent, 0, "%")}</span></td>
                                        <td><span class="badge rounded-pill ${level.badge}">${escapeHtml(level.label)}</span></td>
                                    </tr>
                                `;
                            }).join("")}
                        </tbody>
                    </table>
                </div>
            `}
        </div>
    `;
}

export function summarizeSiteUpgradeAnalysis(payload, siteId) {
    const analysis = buildSiteUpgradeAnalysis(payload, siteId);
    const siteRow = analysis.siteRow;
    if (!siteRow) {
        return analysis.capacityCircuitCount > 0
            ? "No upgrade candidates matched this site in the selected period."
            : "No usable service-plan limits were available for upgrade analysis.";
    }
    const meta = priorityMeta(siteRow);
    return `${meta.title}: ${fmt(siteRow.candidate_count)} candidate ${plural(siteRow.candidate_count, "circuit")}, ${fmt(siteRow.urgent_count)} urgent and ${fmt(siteRow.watch_count)} watch.`;
}
